import React, { useState } from "react";
import axios from "axios";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import MoneyInput from "./MoneyInput";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { toast } from "sonner";
import { Link2, Download, ArrowRight, AlertTriangle, ExternalLink, Copy } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SOURCE_LABEL = {
  "sahibinden.com": "Sahibinden.com",
  "hepsiemlak.com": "Hepsiemlak.com",
  "emlakjet.com": "Emlakjet.com",
};

const ImportListingDialog = ({ open, onClose, onSaved, initialDraft = null }) => {
  const [step, setStep] = useState("input"); // input | preview
  const [url, setUrl] = useState("");
  const [fetching, setFetching] = useState(false);
  const [preview, setPreview] = useState(null); // full API response
  const [form, setForm] = useState({}); // editable form fields
  const [selectedPhotos, setSelectedPhotos] = useState({});
  const [saving, setSaving] = useState(false);

  // Prefill from Chrome-extension draft when provided
  React.useEffect(() => {
    if (!initialDraft || !open) return;
    const d = initialDraft.data || {};
    const previewObj = {
      ok: true,
      source: d.source || d.source_host,
      listing_id: d.listing_id || null,
      url: d.source_url,
      raw_title: d.title || "",
      confidence: null,
      photos: d.photos || [],
      duplicate: initialDraft.duplicate || { exists: false },
    };
    setPreview(previewObj);
    setForm({
      title: d.title || "",
      listing_url: d.source_url || "",
      listing_source: d.source || d.source_host || "",
      listing_id: d.listing_id || "",
      category: "gezeceklerim",
      property_type: d.property_type || "",
      price: d.price ?? "",
      currency: d.currency || "TRY",
      city: d.city || "",
      district: d.district || "",
      neighborhood: d.neighborhood || "",
      address: d.location_text || "",
      lat: d.lat ?? "",
      lng: d.lng ?? "",
      gross_area: d.gross_area ?? "",
      net_area: d.net_area ?? "",
      room_count: d.room_count || "",
      building_age: d.building_age ?? "",
      current_floor: d.current_floor || "",
      total_floors: d.total_floors || "",
      heating_type: d.heating_type || "",
      bathrooms: d.bathrooms ?? "",
      balcony: d.balcony || "",
      furnished: d.furnished || "",
      parking: d.parking || "",
      in_complex: d.in_complex || "",
      monthly_dues: d.monthly_dues ?? "",
      deed_status: d.deed_status || "",
      description: d.description || "",
    });
    const initSel = {};
    (d.photos || []).forEach((u, i) => { initSel[u] = i === 0; });
    setSelectedPhotos(initSel);
    setStep("preview");
  }, [initialDraft, open]);

  const reset = () => {
    setStep("input");
    setUrl("");
    setPreview(null);
    setForm({});
    setSelectedPhotos({});
    setFetching(false);
    setSaving(false);
  };

  const handleClose = () => { reset(); onClose(); };

  const fetchPreview = async () => {
    const trimmed = url.trim();
    if (!/^https?:\/\/.+/.test(trimmed)) {
      toast.error("Geçerli bir URL girin (https://...)");
      return;
    }
    setFetching(true);
    try {
      const { data } = await axios.post(
        `${API_URL}/api/import/preview`,
        { url: trimmed },
        { withCredentials: true }
      );
      setPreview(data);
      if (data.duplicate?.exists) {
        toast.warning("Bu ilan daha önce eklenmiş olabilir");
      }
      if (data.ok) {
        const ex = data.extracted || {};
        // Prefill form. Do NOT prefill lat/lng if zero.
        setForm({
          title: data.raw_title || "",
          listing_url: data.url || trimmed,
          listing_source: data.source,
          listing_id: data.listing_id || "",
          category: "gezeceklerim",
          property_type: ex.property_type || "",
          price: ex.price || "",
          currency: ex.currency || "TRY",
          city: ex.city || "",
          district: ex.district || "",
          neighborhood: ex.neighborhood || "",
          address: ex.address || "",
          lat: ex.lat || "",
          lng: ex.lng || "",
          gross_area: ex.gross_area || "",
          net_area: ex.net_area || "",
          room_count: ex.room_count || "",
          building_age: ex.building_age || "",
          current_floor: ex.current_floor || "",
          total_floors: ex.total_floors || "",
          heating_type: ex.heating_type || "",
          bathrooms: ex.bathrooms || "",
          balcony: ex.balcony || "",
          furnished: ex.furnished || "",
          parking: ex.parking || "",
          in_complex: ex.in_complex || "",
          monthly_dues: ex.monthly_dues || "",
          deed_status: ex.deed_status || "",
          description: ex.description || "",
        });
        // Auto-select the first photo as main
        const initial = {};
        (data.photos || []).forEach((u, i) => { initial[u] = i === 0; });
        setSelectedPhotos(initial);
        setStep("preview");
      } else {
        // Manual fallback
        toast.info(data.reason || "Otomatik veri çekilemedi, manuel doldurabilirsiniz.");
        setForm({ title: "", listing_url: trimmed, listing_source: data.source, category: "gezeceklerim" });
        setStep("preview");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "İstek başarısız");
    }
    setFetching(false);
  };

  const save = async (force = false) => {
    if (!form.title?.trim()) {
      toast.error("Başlık zorunludur");
      return;
    }
    setSaving(true);
    try {
      const body = { ...form, force,
        selected_photo_urls: Object.entries(selectedPhotos).filter(([, v]) => v).map(([k]) => k) };
      // Coerce numeric fields — building_age/current_floor/total_floors
      // are TEXT (ranges like "11-15 arası", "Yüksek Giriş" survive) and
      // must NOT be coerced to Number.
      ["price","lat","lng","gross_area","net_area","bathrooms",
       "monthly_dues"].forEach(k => {
        if (body[k] === "") body[k] = null;
        else if (body[k] != null) body[k] = Number(body[k]) || null;
      });
      // Text-only fields: null out only if empty string.
      ["building_age","current_floor","total_floors"].forEach(k => {
        if (body[k] === "" || body[k] == null) body[k] = null;
        else body[k] = String(body[k]);
      });
      await axios.post(`${API_URL}/api/import/save`, body, { withCredentials: true });
      toast.success("İlan kaydedildi");
      onSaved && onSaved();
      handleClose();
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (e.response?.status === 409 && detail?.duplicate?.exists) {
        if (window.confirm("Bu ilan daha önce eklenmiş görünüyor. Yine de kaydedilsin mi?")) {
          await save(true);
          return;
        }
      } else {
        toast.error(typeof detail === "string" ? detail : "Kaydedilemedi");
      }
    }
    setSaving(false);
  };

  const setField = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  return (
    <Dialog open={open} onOpenChange={(v) => !v && handleClose()}>
      <DialogContent
        data-testid="import-listing-dialog"
        className={`w-full ${step === "input" ? "sm:max-w-lg sm:!h-auto" : "sm:max-w-3xl sm:!h-auto"} h-full sm:!max-h-[calc(100dvh-24px)] bg-white dark:bg-[#18181B] border-[#E4E4E7] dark:border-[#27272A] overflow-hidden !flex !flex-col sm:!grid-cols-none sm:!gap-0 !p-0`}
        style={{ maxHeight: "calc(100dvh - 24px)" }}
      >
        <DialogHeader className="p-4 sm:p-6 border-b border-[#E4E4E7] dark:border-[#27272A]">
          <DialogTitle className="flex items-center gap-2 text-lg">
            <Link2 className="w-5 h-5 text-[#F59E0B]" />
            İlan Linkinden Ekle
          </DialogTitle>
          <DialogDescription className="sr-only">
            İlan URL'sini yapıştırarak ya da Chrome eklentisinden gönderilmiş
            bir aktarım taslağını gözden geçirerek yeni gayrimenkul kaydı oluşturun.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-4">
          {step === "input" && (
            <div className="space-y-4">
              <Label>İlan Bağlantısı</Label>
              <Input
                data-testid="import-url-input"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder="https://www.sahibinden.com/ilan/... veya hepsiemlak / emlakjet URL'i"
                className="w-full"
                autoFocus
              />
              <div className="text-xs text-[#52525B] dark:text-[#A1A1AA]">
                Desteklenen kaynaklar: sahibinden.com, hepsiemlak.com, emlakjet.com ve JSON-LD/Open Graph verisi sunan diğer siteler.
                CAPTCHA, giriş duvarı veya anti-bot koruması olan sayfalarda otomatik veri çekilemeyebilir — o durumda formu manuel doldurabilirsiniz.
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={handleClose}>İptal</Button>
                <Button
                  data-testid="import-fetch-button"
                  onClick={fetchPreview}
                  disabled={fetching || !url.trim()}
                  className="bg-[#F59E0B] hover:bg-[#D97706] text-white disabled:bg-[#F59E0B]/60 disabled:text-white/90 dark:disabled:bg-[#F59E0B]/40 dark:disabled:text-white/80"
                >
                  <Download className="w-4 h-4 mr-2" />
                  {fetching ? "Getiriliyor..." : "Bilgileri Çek"}
                </Button>
              </div>
            </div>
          )}

          {step === "preview" && (
            <div className="space-y-4">
              {/* Source badges */}
              {preview?.source && (
                <div className="flex items-center gap-2 text-sm">
                  <span className="px-2 py-0.5 rounded bg-[#F59E0B]/10 text-[#B45309] dark:text-[#F59E0B] font-medium">
                    {SOURCE_LABEL[preview.source] || preview.source}
                  </span>
                  {preview.listing_id && (
                    <span className="text-[#52525B] dark:text-[#A1A1AA]">
                      İlan No: {preview.listing_id}
                    </span>
                  )}
                  {preview.confidence != null && (
                    <span className="ml-auto text-xs text-[#52525B] dark:text-[#A1A1AA]">
                      Veri güvenilirliği: <strong>{preview.confidence}%</strong>
                    </span>
                  )}
                </div>
              )}

              {/* URL row */}
              <div className="flex items-center gap-2 p-2 rounded border border-[#E4E4E7] dark:border-[#27272A] bg-[#F9FAFB] dark:bg-[#09090B]">
                <ExternalLink className="w-4 h-4 text-[#52525B]" />
                <a href={form.listing_url} target="_blank" rel="noreferrer"
                   className="text-xs text-[#F59E0B] hover:underline truncate flex-1"
                   data-testid="import-preview-link">{form.listing_url}</a>
                <button
                  onClick={() => { navigator.clipboard.writeText(form.listing_url); toast.success("Kopyalandı"); }}
                  className="p-1 text-[#52525B] hover:text-[#0A0A0A] dark:hover:text-[#FAFAFA]"
                  aria-label="Kopyala"
                >
                  <Copy className="w-4 h-4" />
                </button>
              </div>

              {/* Duplicate warning */}
              {preview?.duplicate?.exists && (
                <div className="p-3 rounded border border-amber-300 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-700 flex gap-2">
                  <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
                  <div className="text-xs text-amber-800 dark:text-amber-200">
                    Bu ilan daha önce eklenmiş olabilir ({preview.duplicate.match_by} eşleşme).
                    Kaydettiğinizde onay istenecek.
                  </div>
                </div>
              )}

              {/* Extraction failed */}
              {!preview?.ok && preview?.reason && (
                <div className="p-3 rounded border border-red-300 bg-red-50 dark:bg-red-900/20 dark:border-red-700 text-xs text-red-800 dark:text-red-200">
                  {preview.reason}
                </div>
              )}

              {/* Editable form */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Field label="Başlık *" testid="import-form-title">
                  <Input value={form.title || ""} onChange={setField("title")} data-testid="import-form-title-input" />
                </Field>
                <Field label="Kategori" testid="import-form-category">
                  <select value={form.category || "gezeceklerim"} onChange={setField("category")}
                          className="w-full border border-[#E4E4E7] dark:border-[#27272A] bg-white dark:bg-[#09090B] rounded px-2 py-2 text-sm">
                    <option value="gezeceklerim">Gezeceklerim</option>
                    <option value="gezdiklerim">Gezdiklerim</option>
                    <option value="sahiplerim">Sahip Olduklarım</option>
                  </select>
                </Field>
                <Field label="Fiyat">
                  <MoneyInput
                    value={form.price}
                    onChange={(n) => setForm(f => ({ ...f, price: n == null ? "" : n }))}
                    currency={form.currency || "TRY"}
                  />
                </Field>
                <Field label="Para Birimi">
                  <select value={form.currency || "TRY"} onChange={setField("currency")}
                          className="w-full border border-[#E4E4E7] dark:border-[#27272A] bg-white dark:bg-[#09090B] rounded px-2 py-2 text-sm">
                    <option>TRY</option><option>EUR</option><option>USD</option><option>GBP</option>
                  </select>
                </Field>
                <Field label="Şehir"><Input value={form.city || ""} onChange={setField("city")} /></Field>
                <Field label="İlçe"><Input value={form.district || ""} onChange={setField("district")} /></Field>
                <Field label="Mahalle"><Input value={form.neighborhood || ""} onChange={setField("neighborhood")} /></Field>
                <Field label="Adres"><Input value={form.address || ""} onChange={setField("address")} /></Field>
                <Field label="Enlem"><Input type="number" step="any" value={form.lat || ""} onChange={setField("lat")} /></Field>
                <Field label="Boylam"><Input type="number" step="any" value={form.lng || ""} onChange={setField("lng")} /></Field>
                <Field label="Gayrimenkul Tipi"><Input value={form.property_type || ""} onChange={setField("property_type")} /></Field>
                <Field label="Oda Sayısı"><Input value={form.room_count || ""} onChange={setField("room_count")} /></Field>
                <Field label="Net m²"><Input type="number" value={form.net_area || ""} onChange={setField("net_area")} /></Field>
                <Field label="Brüt m²"><Input type="number" value={form.gross_area || ""} onChange={setField("gross_area")} /></Field>
                <Field label="Bina Yaşı"><Input data-testid="import-form-building-age-input" placeholder='örn. 5 veya 11-15 arası' value={form.building_age || ""} onChange={setField("building_age")} /></Field>
                <Field label="Bulunduğu Kat"><Input data-testid="import-form-current-floor-input" placeholder='örn. 3 veya Yüksek Giriş' value={form.current_floor || ""} onChange={setField("current_floor")} /></Field>
                <Field label="Toplam Kat"><Input data-testid="import-form-total-floors-input" value={form.total_floors || ""} onChange={setField("total_floors")} /></Field>
                <Field label="Isıtma"><Input value={form.heating_type || ""} onChange={setField("heating_type")} /></Field>
                <Field label="Banyo Sayısı"><Input type="number" value={form.bathrooms || ""} onChange={setField("bathrooms")} /></Field>
                <Field label="Balkon"><Input value={form.balcony || ""} onChange={setField("balcony")} /></Field>
                <Field label="Eşyalı"><Input value={form.furnished || ""} onChange={setField("furnished")} /></Field>
                <Field label="Otopark"><Input value={form.parking || ""} onChange={setField("parking")} /></Field>
                <Field label="Site İçerisinde"><Input value={form.in_complex || ""} onChange={setField("in_complex")} /></Field>
                <Field label="Aidat"><Input type="number" value={form.monthly_dues || ""} onChange={setField("monthly_dues")} /></Field>
                <Field label="Tapu Durumu"><Input value={form.deed_status || ""} onChange={setField("deed_status")} /></Field>
              </div>
              <div>
                <Label>Açıklama</Label>
                <Textarea rows={3} value={form.description || ""} onChange={setField("description")} />
              </div>

              {/* Photos picker */}
              {preview?.photos?.length > 0 && (
                <div>
                  <Label className="block mb-2">Fotoğraflar — kaydedilecekleri seçin</Label>
                  <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                    {preview.photos.map((u, i) => {
                      const selected = !!selectedPhotos[u];
                      return (
                        <label key={u} className={`relative cursor-pointer rounded overflow-hidden border-2 ${selected ? "border-[#F59E0B]" : "border-transparent"}`}
                               data-testid={`import-photo-${i}`}>
                          <img src={u} alt="preview" className="w-full h-24 object-cover" />
                          <input type="checkbox" checked={selected}
                                 onChange={(e) => setSelectedPhotos(sp => ({ ...sp, [u]: e.target.checked }))}
                                 className="absolute top-1 right-1 accent-[#F59E0B]"
                                 data-testid={`import-photo-checkbox-${i}`} />
                        </label>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {step === "preview" && (
          <div className="border-t border-[#E4E4E7] dark:border-[#27272A] p-3 sm:p-4 flex items-center justify-between gap-2"
               style={{ paddingBottom: "calc(env(safe-area-inset-bottom) + 12px)" }}>
            <Button variant="outline" onClick={() => setStep("input")}>← URL değiştir</Button>
            <Button
              data-testid="import-save-button"
              onClick={() => save(false)}
              disabled={saving}
              className="bg-[#F59E0B] hover:bg-[#D97706] text-white"
            >
              {saving ? "Kaydediliyor..." : "Kaydet"}
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

const Field = ({ label, children }) => (
  <div>
    <Label className="text-xs text-[#52525B] dark:text-[#A1A1AA]">{label}</Label>
    <div className="mt-1">{children}</div>
  </div>
);

export default ImportListingDialog;
